import { useEffect, useMemo, useState } from 'react';
import { dateRange, weekdayTc, isWeekend, shortMonthDay, parseIso } from '../lib/dates';
import type { GanttData } from '../types';

export interface CellRange {
  group: string;
  startDate: string;
  endDate: string;
  /** Existing items in the first cell (for picker pre-fill) */
  initialItems: string[];
  /** Pixel anchor for popover */
  anchor: { x: number; y: number };
}

interface Props {
  startDate: string;
  endDate: string;
  groups: string[];
  data: GanttData;
  holidaySet: Set<string>;
  onRangePick: (range: CellRange) => void;
}

const ROW_H = 40; // 觸控 target ≥40px（甘特格密度考量）
const DAY_W = 40;
const LABEL_W = 100;

export function GanttGrid({ startDate, endDate, groups, data, holidaySet, onRangePick }: Props) {
  const days = useMemo(() => dateRange(startDate, endDate), [startDate, endDate]);
  const dayIndex = useMemo(() => {
    const m = new Map<string, number>();
    days.forEach((d, i) => m.set(d, i));
    return m;
  }, [days]);

  const [drag, setDrag] = useState<null | {
    group: string;
    startDate: string;
    currentDate: string;
  }>(null);

  // Globally listen for pointerup so drags ending outside a cell still finalize.
  // pointercancel（touch 捲動接管等）則放棄本次拖拉。
  useEffect(() => {
    if (!drag) return;
    const onUp = (e: PointerEvent) => {
      const start = drag.startDate;
      const end = drag.currentDate;
      const lo = start <= end ? start : end;
      const hi = start <= end ? end : start;
      const initialItems = data[drag.group]?.[lo] ?? [];
      onRangePick({
        group: drag.group,
        startDate: lo,
        endDate: hi,
        initialItems,
        anchor: { x: e.clientX + 8, y: e.clientY + 8 },
      });
      setDrag(null);
    };
    const onCancel = () => setDrag(null);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [drag, data, onRangePick]);

  if (days.length === 0) {
    return (
      <div className="rounded-lg border border-tb-border bg-tb-surface p-8 text-center text-sm text-tb-text2">
        請先在上方填入「開工日」和「預計完工日」，才會顯示甘特圖網格。
      </div>
    );
  }

  function inDragRange(group: string, date: string): boolean {
    if (!drag || drag.group !== group) return false;
    const a = dayIndex.get(drag.startDate);
    const b = dayIndex.get(drag.currentDate);
    const c = dayIndex.get(date);
    if (a == null || b == null || c == null) return false;
    const lo = Math.min(a, b), hi = Math.max(a, b);
    return c >= lo && c <= hi;
  }

  return (
    <div className="rounded-lg border border-tb-border bg-tb-surface shadow-sm overflow-auto select-none">
      <div className="inline-block min-w-full" role="grid" aria-label="甘特圖網格">
        {/* Header */}
        <div className="flex sticky top-0 bg-tb-bg border-b border-tb-border z-10" role="row">
          <div className="shrink-0 border-r border-tb-border bg-tb-bg" style={{ width: LABEL_W }} role="columnheader" aria-label="工種">
            <div className="h-8 flex items-center justify-center text-xs font-semibold text-tb-text2">日期</div>
            <div className="h-6 flex items-center justify-center text-[10px] text-tb-text2">星期</div>
          </div>
          <div className="flex">
            {days.map(d => {
              const wknd = isWeekend(d);
              const holi = holidaySet.has(d);
              const monthFirst = parseIso(d)?.getDate() === 1;
              return (
                <div
                  key={d}
                  role="columnheader"
                  className={[
                    'shrink-0 border-r text-center',
                    monthFirst ? 'border-l-2 border-l-tb-text3' : 'border-tb-surface2',
                    holi ? 'bg-tb-rust-bg' : wknd ? 'bg-tb-surface2' : 'bg-tb-bg',
                  ].join(' ')}
                  style={{ width: DAY_W }}
                  title={holi ? '國定假日' : wknd ? '週末' : ''}
                >
                  <div className={`h-8 flex items-center justify-center text-[11px] font-medium ${holi ? 'text-tb-rust' : 'text-tb-text'}`}>
                    {shortMonthDay(d)}
                  </div>
                  <div className={`h-6 flex items-center justify-center text-[10px] ${holi ? 'text-tb-rust' : wknd ? 'text-tb-text2' : 'text-tb-text3'}`}>
                    {weekdayTc(d)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rows */}
        {groups.map(group => (
          <div key={group} className="flex border-b border-tb-surface2" role="row">
            <div
              role="rowheader"
              className="shrink-0 border-r border-tb-border bg-tb-surface sticky left-0 flex items-center px-3 text-sm font-medium text-tb-text z-[5]"
              style={{ width: LABEL_W, height: ROW_H }}
            >
              {group}
            </div>
            <div className="flex">
              {days.map(d => {
                const wknd = isWeekend(d);
                const holi = holidaySet.has(d);
                const items = data[group]?.[d] ?? [];
                const inDrag = inDragRange(group, d);
                const filled = items.length > 0;
                return (
                  <div
                    key={d}
                    role="gridcell"
                    tabIndex={0}
                    aria-label={`${group} ${shortMonthDay(d)}${filled ? `，${items.join('、')}` : '，未填'}`}
                    onPointerDown={(e) => {
                      if (e.pointerType === 'mouse' && e.button !== 0) return;
                      // touch/pen 預設有 implicit pointer capture，釋放掉才會對其他格觸發 pointerenter
                      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                        e.currentTarget.releasePointerCapture(e.pointerId);
                      }
                      setDrag({ group, startDate: d, currentDate: d });
                    }}
                    onPointerEnter={() => {
                      if (drag && drag.group === group) {
                        setDrag({ ...drag, currentDate: d });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const rect = e.currentTarget.getBoundingClientRect();
                        onRangePick({
                          group,
                          startDate: d,
                          endDate: d,
                          initialItems: items,
                          anchor: { x: rect.left, y: rect.bottom + 4 },
                        });
                      }
                    }}
                    className={[
                      'shrink-0 border-r text-[10px] flex items-center justify-center px-0.5 overflow-hidden cursor-pointer transition-colors',
                      'touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-yellow',
                      inDrag
                        ? 'bg-tb-sky-bg border-tb-sky-border'
                        : filled
                          ? 'bg-tb-moss-bg border-tb-moss-border hover:bg-tb-moss-bg-hover'
                          : holi
                            ? 'bg-tb-rust-bg border-tb-surface2 hover:bg-tb-rust-bg-hover'
                            : wknd
                              ? 'bg-tb-bg/80 border-tb-surface2 hover:bg-tb-surface2'
                              : 'bg-tb-surface border-tb-surface2 hover:bg-tb-bg',
                    ].join(' ')}
                    style={{ width: DAY_W, height: ROW_H }}
                    title={items.length ? items.join(', ') : '點擊或拖拉填入工項'}
                  >
                    {filled && (
                      <span className="truncate text-tb-moss leading-tight font-medium">
                        {items.join(',')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
